import React, { useContext, useEffect, useState, useMemo } from "react";
import Logo from "../../assets/images/sblogo.png";
import { MyContext } from "../../App";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { Link, useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { postData } from "../../utils/api";
import { setAuthenticated, setJwtToken } from "../../store/slices/AuthSlice";
import { useDispatch } from "react-redux";
import { Helmet } from "react-helmet-async";
import { fetchCartItems } from "../../store/slices/CartSlice";
import { fetchWishlistItems } from "../../store/slices/WishlistSlice";
import { fetchOrders } from "../../store/slices/OrdersSlice";
import { toast } from "react-toastify";

const SignIn = () => {
  const [step, setStep] = useState(1); 
  const [isLoading, setIsLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [onboardFields, setOnboardFields] = useState({ name: "", email: "" });
  const [user, setUser] = useState(null);
  const [token, setToken] = useState("");
  const otpRefs = useMemo(() => Array.from({ length: 6 }, () => React.createRef()), []);
  const [otpArray, setOtpArray] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(0);

  const context = useContext(MyContext);
  const history = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    context.setIsHeaderFooterShow(false);
    context.setEnableFilterTab(false);
  }, []);

  // Resend OTP timer logic
  useEffect(() => {
    let timer;
    if (step === 2 && resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendTimer, step]);

  const handleResendOtp = async () => {
    let formattedPhone = phone.trim();
    if (!formattedPhone.startsWith('+')) {
      formattedPhone = '+91' + formattedPhone.replace(/^0+/, '');
    }
    setIsLoading(true);
    try {
      const res = await postData("/api/user/request-otp", { phoneNumber: formattedPhone });
      if (res && res.message && res.message.includes("OTP sent")) {
        toast.success("OTP resent to your phone.");
        setResendTimer(60);
      } else {
        toast.error(res.message || "Failed to resend OTP.");
      }
    } catch (error) {
      toast.error("Failed to resend OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1: Request OTP
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!phone) {
      context.setAlertBox({ open: true, error: true, msg: "Phone number cannot be blank!" });
      return;
    }
    let formattedPhone = phone.trim();
    if (!formattedPhone.startsWith('+')) {
      formattedPhone = '+91' + formattedPhone.replace(/^0+/, '');
    }
    setIsLoading(true);
    try {
      const res = await postData("/api/user/request-otp", { phoneNumber: formattedPhone });
      if (res && res.message && res.message.includes("OTP sent")) {
        context.setAlertBox({ open: true, error: false, msg: "OTP sent to your phone." });
        setStep(2);
        setResendTimer(60); // Start timer on entering step 2
      } else {
        context.setAlertBox({ open: true, error: true, msg: res.message || "Failed to send OTP." });
      }
    } catch (error) {
      context.setAlertBox({ open: true, error: true, msg: error?.response?.data?.message || "Failed to send OTP." });
    } finally {
      setIsLoading(false);
    }
  };

  // OTP input handlers
  const handleOtpBoxChange = (e, idx) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    const newArr = [...otpArray];
    newArr[idx] = val; // Allow empty value to clear digit
    setOtpArray(newArr);
    if (val && idx < 5) {
      otpRefs[idx + 1].current.focus();
    }
  };

  const handleOtpBoxKeyDown = (e, idx) => {
    if (e.key === "Backspace" && idx > 0 && !otpArray[idx]) {
      const newArr = [...otpArray];
      newArr[idx - 1] = ""; // Clear previous digit
      setOtpArray(newArr);
      otpRefs[idx - 1].current.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 6);
    if (paste.length === 6) {
      setOtpArray(paste.split(""));
      otpRefs[5].current.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otp = otpArray.join("");
    if (otp.length !== 6) {
      context.setAlertBox({ open: true, error: true, msg: "Enter 6 digit OTP!" });
      return;
    }

    let formattedPhone = phone.trim();
    if (!formattedPhone.startsWith('+')) {
      formattedPhone = '+91' + formattedPhone.replace(/^0+/, '');
    }

    setIsLoading(true);
    try {
      const res = await postData("/api/user/verify-otp", { phoneNumber: formattedPhone, otp });
      if (res && res.token && res.user) {
        localStorage.setItem("token", res.token);
        setToken(res.token);
        dispatch(setJwtToken(res.token));
        dispatch(setAuthenticated(true));
        setUser(res.user);
        if (!res.user.name || res.user.onboardingComplete === false) {
          setStep(3);
        } else {
          const userObj = {
            name: res.user?.name,
            email: res.user?.email,
            userId: res.user?._id,
            image: res.user?.image,
          };
          localStorage.setItem("user", JSON.stringify(userObj));
          context.setUser(userObj);
          dispatch(fetchCartItems(res.user._id));
          dispatch(fetchWishlistItems(res.user._id));
          dispatch(fetchOrders(res.user._id));
          context.setAlertBox({ open: true, error: false, msg: "Login successful!" });
          setTimeout(() => {
            history("/");
            context.setIsLogin(true);
            context.setIsHeaderFooterShow(true);
          }, 1000);
        }
      } else {
        context.setAlertBox({ open: true, error: true, msg: res.message || "Invalid OTP." });
      }
    } catch (error) {
      context.setAlertBox({ open: true, error: true, msg: "Failed to verify OTP." });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Onboarding
  const handleOnboard = async (e) => {
    e.preventDefault();
    if (!onboardFields.name) {
      context.setAlertBox({ open: true, error: true, msg: "Name is required." });
      return;
    }
    setIsLoading(true);
    try {
      const res = await postData(
        "/api/user/onboard",
        { name: onboardFields.name, email: onboardFields.email },
        { headers: { Authorization: `Bearer ${token || localStorage.getItem("token")}` } }
      );
      if (res && res.success && res.user) {
        const userObj = {
          name: res.user?.name,
          email: res.user?.email,
          userId: res.user?._id,
          image: res.user?.image,
        };
        localStorage.setItem("user", JSON.stringify(userObj));
        context.setUser(userObj);
        dispatch(fetchCartItems(res.user._id));
        dispatch(fetchWishlistItems(res.user._id));
        dispatch(fetchOrders(res.user._id));
        context.setAlertBox({ open: true, error: false, msg: "Welcome! Onboarding complete." });
        setTimeout(() => {
          history("/");
          context.setIsLogin(true);
          context.setIsHeaderFooterShow(true);
        }, 1000);
      } else {
        context.setAlertBox({ open: true, error: true, msg: res.message || "Failed to complete onboarding." });
      }
    } catch (error) {
      context.setAlertBox({ open: true, error: true, msg: "Failed to complete onboarding." });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="section signInPage min-h-screen bg-gray-50 flex items-center justify-center py-6">
      <Helmet>
        <title>Sign In - SabbaFarm</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="Secure login page for Sabba Farm customers." />
        <link rel="canonical" href="https://sabbafarm.com/signIn" />
      </Helmet>
      <div className="container mx-auto px-4">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6">
            <div className="text-center mb-6">
              <img src={Logo} className="w-32 h-32 mx-auto" alt="Sabba Farm Logo" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {step === 1 ? "Welcome Back" : step === 2 ? "Enter OTP" : "Complete Your Profile"}
              </h2>
              <p className="text-gray-600 text-sm">
                {step === 1 && "Sign in to continue to SabbaFarm"}
                {step === 2 && `OTP sent to ${phone}`}
                {step === 3 && "Just a few more details to get started!"}
              </p>
            </div>
            {step === 1 && (
              <form onSubmit={handleRequestOtp} className="space-y-4">
                <TextField
                  id="phone"
                  label="Phone Number"
                  type="tel"
                  required
                  variant="outlined"
                  fullWidth
                  name="phone"
                  onChange={e => setPhone(e.target.value)}
                  className="bg-white"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": { borderColor: "#16a34a" },
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#16a34a" },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  className="bg-green-600 hover:bg-green-700 text-white py-2 rounded-md text-base font-medium"
                  disabled={isLoading}
                  sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" } }}
                >
                  {isLoading ? <CircularProgress size={20} color="inherit" /> : "Send OTP"}
                </Button>
              </form>
            )}
            {step === 2 && (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div className="flex justify-between gap-2" onPaste={handleOtpPaste}>
                  {otpArray.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={otpRefs[idx]}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      className="w-10 h-12 text-center border rounded text-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                      value={digit}
                      onChange={e => handleOtpBoxChange(e, idx)}
                      onKeyDown={e => handleOtpBoxKeyDown(e, idx)}
                    />
                  ))}
                </div>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  className="bg-green-600 hover:bg-green-700 text-white py-2 rounded-md text-base font-medium"
                  disabled={isLoading}
                  sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" } }}
                >
                  {isLoading ? <CircularProgress size={20} color="inherit" /> : "Verify OTP"}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  className="mt-2"
                  onClick={handleResendOtp}
                  disabled={resendTimer > 0 || isLoading}
                >
                  {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Resend OTP"}
                </Button>
                <Button
                  variant="text"
                  fullWidth
                  className="text-green-700 mt-4"
                  onClick={() => setStep(1)}
                  disabled={isLoading}
                >
                  Change Number
                </Button>
              </form>
            )}
            {step === 3 && (
              <form onSubmit={handleOnboard} className="space-y-4">
                <TextField
                  id="name"
                  label="Name"
                  type="text"
                  required
                  variant="outlined"
                  fullWidth
                  name="name"
                  value={onboardFields.name}
                  onChange={e => setOnboardFields(f => ({ ...f, name: e.target.value }))}
                  className="bg-white"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": { borderColor: "#16a34a" },
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#16a34a" },
                  }}
                />
                <TextField
                  id="email"
                  label="Email (optional)"
                  type="email"
                  variant="outlined"
                  fullWidth
                  name="email"
                  value={onboardFields.email}
                  onChange={e => setOnboardFields(f => ({ ...f, email: e.target.value }))}
                  className="bg-white"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": { borderColor: "#16a34a" },
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#16a34a" },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  className="bg-green-600 hover:bg-green-700 text-white py-2 rounded-md text-base font-medium"
                  disabled={isLoading}
                  sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" } }}
                >
                  {isLoading ? <CircularProgress size={20} color="inherit" /> : "Complete Onboarding"}
                </Button>
                <Button
                  variant="text"
                  fullWidth
                  className="text-green-700 mt-2"
                  onClick={() => setStep(1)}
                  disabled={isLoading}
                >
                  Go back to Sign In
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignIn;